import { RecordsDiff, structuredClone, TLRecord } from "tldraw";
import { debugWarn } from "@/utils/debugLog";
import { AgentAction } from "../../../shared/types/AgentAction";
import { ChatHistoryItem } from "../../../shared/types/ChatHistoryItem";
import { Streaming } from "../../../shared/types/Streaming";
import {
  AgentActionUtil,
  getAgentActionUtilsRecordForMode,
} from "../../actions/AgentActionUtil";
import { AgentHelpers } from "../../AgentHelpers";
import type { TldrawAgent } from "../TldrawAgent";
import { BaseAgentManager } from "./BaseAgentManager";

/**
 * Manages action-related functionality for the agent.
 * Handles action utils, action execution, and chat history updates for actions.
 */
export class AgentActionManager extends BaseAgentManager {
  /**
   * A record of the agent's action util instances.
   * Used by the `getAgentActionUtil` method.
   */
  private agentActionUtils: Record<
    AgentAction["_type"],
    AgentActionUtil<AgentAction>
  >;

  /** Returned by `getAgentActionUtil` for an action type with no util. */
  unknownActionUtil: AgentActionUtil<AgentAction>;

  constructor(agent: TldrawAgent) {
    super(agent);
    this.agentActionUtils = getAgentActionUtilsRecordForMode(
      this.agent,
      agent.mode.getCurrentModeType(),
    );
    this.unknownActionUtil = this.agentActionUtils.unknown;
  }

  /**
   * Reset the action manager to its initial state.
   * Currently no state to reset as action utils are stateless.
   */
  reset(): void {
    // Reset state if needed - currently no state to reset
  }

  /**
   * Rebuild action utils for a specific mode.
   * Called when the agent's mode changes to ensure mode-specific
   * action utils are used.
   *
   * @param mode - The mode to rebuild utils for.
   */
  rebuildUtilsForMode(mode: string): void {
    this.agentActionUtils = getAgentActionUtilsRecordForMode(this.agent, mode);
    this.unknownActionUtil = this.agentActionUtils.unknown;
  }

  /**
   * Get an agent action util for a specific action type.
   *
   * @param type - The type of action to get the util for.
   * @returns The action util.
   */
  getAgentActionUtil(type?: string) {
    const utilType = this.getAgentActionUtilType(type);
    return this.agentActionUtils[utilType];
  }

  /**
   * Get the util type for a provided action type.
   * If no util type is found, returns 'unknown'.
   * @param type - The action type to get the util type for.
   * @returns The action util type, or 'unknown' if not found.
   */
  getAgentActionUtilType(type?: string): AgentAction["_type"] {
    if (!type) return "unknown";
    const util = this.agentActionUtils[type as AgentAction["_type"]];
    if (!util) return "unknown";
    return type as AgentAction["_type"];
  }

  /**
   * Make the agent perform an action.
   * Applies the action to the editor and tracks it in chat history.
   * @param action - The action to make the agent do.
   * @param helpers - The helpers to use for action execution.
   * @returns An object containing the diff of changes made and a promise that resolves when the action completes.
   */
  act(
    action: Streaming<AgentAction>,
    helpers: AgentHelpers = new AgentHelpers(this.agent),
  ): {
    diff: RecordsDiff<TLRecord>;
    promise: Promise<void> | null;
    failed: boolean;
  } {
    const { editor } = this.agent;
    const util = this.getAgentActionUtil(action._type);
    this.agent.setIsActingOnEditor(true);

    let promise: Promise<void> | null = null;
    let diff: RecordsDiff<TLRecord>;
    let failed = false;
    try {
      diff = editor.store.extractingChanges(() => {
        promise = util.applyAction(structuredClone(action), helpers) ?? null;
      });
    } catch (error) {
      // Log and surface the error but do NOT re-throw — a single bad action
      // must not crash the entire tldraw canvas / error-boundary tree.
      debugWarn("[AgentActionManager] action failed, skipping:", error);
      this.agent.onError(error);
      promise = null;
      diff = { added: {} as any, removed: {} as any, updated: {} as any };
      failed = true;
    } finally {
      this.agent.setIsActingOnEditor(false);
    }

    // Out-of-turn page-capture actions (inject or buffered) name their photo.
    const captureId = (action as { captureId?: unknown }).captureId;
    if (!failed && action.complete && typeof captureId === "string") {
      this.agent.markPageCaptureDigitized(captureId);
    }

    // Add the action to chat history only if it didn't fail — failed actions
    // must not appear in history so the next turn's snapshot stays clean.
    if (!failed && util.savesToHistory()) {
      const historyItem: ChatHistoryItem = {
        type: "action",
        action,
      };

      this.agent.chat.update((historyItems) => {
        // If there are no items, start off the chat history with the first item
        if (historyItems.length === 0) return [historyItem];

        // Find the last EXTERNAL prompt index (ignore prompts from 'self' which are internal state transitions)
        const lastPromptIndex = historyItems.findLastIndex(
          (item) => item.type === "prompt" && item.promptSource !== "self",
        );

        // If the last action is still in progress AND it's after the last external prompt, replace it
        const lastActionHistoryItemIndex = historyItems.findLastIndex(
          (item) => item.type === "action",
        );
        const lastActionHistoryItem =
          lastActionHistoryItemIndex !== -1
            ? historyItems[lastActionHistoryItemIndex]
            : null;
        if (
          lastActionHistoryItem &&
          lastActionHistoryItem.type === "action" &&
          !lastActionHistoryItem.action.complete &&
          (lastPromptIndex === -1 ||
            lastActionHistoryItemIndex > lastPromptIndex)
        ) {
          const newHistoryItems = [...historyItems];
          // Replace the incomplete action with the complete one (timestamp already set above)
          newHistoryItems[lastActionHistoryItemIndex] = historyItem;
          return newHistoryItems;
        } else {
          // Otherwise, just add the new item to the end of the list
          return [...historyItems, historyItem];
        }
      });
    }

    return { diff, promise, failed };
  }
}
